// Cadastro de Tempo de Referência por Etapa (issue #7). Cada referência diz
// "processamos N peças em T minutos" numa Etapa — dá a produtividade esperada,
// base de comparação para o Painel de Gantt (fase seguinte) e para os KPIs.

import { ETAPAS, type Etapa } from "./etapas.ts";
import type { Snapshot, TempoReferencia } from "./snapshot.ts";

export type { TempoReferencia };

/** Etapas que aceitam um tempo de referência ("pronto" é terminal). */
export const ETAPAS_COM_TEMPO: Etapa[] = ETAPAS.filter((e) => e !== "pronto");

/** Unidade em que o usuário informa a duração. O canônico é sempre minutos. */
export type UnidadeTempo = "min" | "h";

export class ErroTempoReferencia extends Error {}

export interface EntradaTempoReferencia {
  /** Quantidade de peças de referência. */
  pecas: number;
  /** Duração para essa quantidade, na unidade indicada. */
  valor: number;
  unidade: UnidadeTempo;
}

/**
 * Define (ou remove, com `entrada` nula) o tempo de referência de uma Etapa.
 */
export function definirTempoReferencia(
  snapshot: Snapshot,
  etapa: Etapa,
  entrada: EntradaTempoReferencia | null,
): Snapshot {
  if (!ETAPAS_COM_TEMPO.includes(etapa)) {
    throw new ErroTempoReferencia("Etapa sem tempo de referência.");
  }
  const outras = snapshot.temposReferencia.filter((t) => t.etapa !== etapa);
  if (entrada == null) {
    return { ...snapshot, temposReferencia: outras };
  }
  const { pecas } = entrada;
  if (!Number.isFinite(pecas) || pecas <= 0) {
    throw new ErroTempoReferencia("Informe uma quantidade de peças maior que zero.");
  }
  if (!Number.isFinite(entrada.valor) || entrada.valor <= 0) {
    throw new ErroTempoReferencia("Informe uma duração maior que zero.");
  }
  const minutos = entrada.unidade === "h" ? entrada.valor * 60 : entrada.valor;
  return {
    ...snapshot,
    temposReferencia: [...outras, { etapa, pecas, minutos }].sort(
      (a, b) => ETAPAS.indexOf(a.etapa) - ETAPAS.indexOf(b.etapa),
    ),
  };
}

export function tempoReferencia(
  snapshot: Pick<Snapshot, "temposReferencia">,
  etapa: Etapa,
): TempoReferencia | null {
  return snapshot.temposReferencia.find((t) => t.etapa === etapa) ?? null;
}

/** Minutos por peça — a métrica normalizada para comparar produtividade. */
export function minutosPorPeca(t: TempoReferencia): number {
  return t.minutos / t.pecas;
}

/** Texto curto: "100 peças em 45 min" / "150 peças em 2,5 h". */
export function descreveTempoReferencia(t: TempoReferencia): string {
  const pc = `${formatarNumero(t.pecas)} peça${t.pecas === 1 ? "" : "s"}`;
  return `${pc} em ${descreveDuracao(t.minutos)}`;
}

/** Minutos → "45 min" (abaixo de 1 h) ou "2,5 h". */
export function descreveDuracao(minutos: number): string {
  if (minutos < 60) return `${formatarNumero(minutos)} min`;
  return `${formatarNumero(minutos / 60)} h`;
}

function formatarNumero(n: number): string {
  return Number(n.toFixed(2)).toLocaleString("pt-BR");
}
